import React, {useMemo} from "react";
import {
    IShipment,
    IShipmentCarrier,
    ShipmentPackageQuickActionsEnum,
    ShipmentPosStatusEnum
} from "../../../types/shipment";
import {IWave} from "../../../types/wave";
import {useTranslation} from "react-i18next";
import _ from "lodash";
import Truck from "@2fd/ant-design-icons/lib/Truck";
import {Badge, Button, Collapse, Radio, RadioChangeEvent, Tag, Tooltip} from "antd";
import ArrowTopRightThinCircleOutline from "@2fd/ant-design-icons/lib/ArrowTopRightThinCircleOutline";
import {CheckOutlined, CloseOutlined, InfoCircleOutlined, MinusOutlined} from "@ant-design/icons";
import NpiDisplayDate from "../../display/date";
import NpiInputShipmentPackageQuickActionSelect from "../../input/select-shipment-package-quick-action";

// Shared ShipmentInfo interface for the header component
interface ShipmentInfo {
    count: number;
    shipCarrierName: string;
    shipTrackingId: string;
    carrierLink: string;
    shipTrackingStatus: string;
    statusColor: string;
    shipTrackingLastUpdateAt: string;
    materialDescription?: string;
    materialAssetType?: string;
}

// Merged ShipmentHeader component that handles both single and multiple package cases
const ShipmentHeader = ({
    count,
    shipCarrierName,
    shipTrackingId,
    carrierLink,
    shipTrackingStatus,
    statusColor,
    shipTrackingLastUpdateAt,
    materialDescription,
    materialAssetType,
}: ShipmentInfo) => {
    const {t} = useTranslation();
    const type = count > 1 ? 'multiple' : 'single';

    // No big style object as requested

    return (
        <div style={{display: 'flex', alignItems: 'center', gap: '16px', flexWrap: "wrap", width: "100%"}}>
            {/* Primary Information Section */}
            <div style={{display: 'flex', alignItems: 'center', gap: '10px', fontWeight: 'bold', flex: "450px", maxWidth: "100%", flexWrap: "wrap"}}>

                {/* Package Count Badge (for multiple) */}
                {type === 'multiple' && count !== undefined && (
                    <Tooltip title={t('INTERNAL.CLIENT.SHIPMENT.SHIPMENT_STATS.TOTAL_PACKAGE')}>
                        <Badge count={count} style={{backgroundColor: '#1890ff', marginRight: '10px'}} title={''}/>
                    </Tooltip>
                )}

                {type === 'single' && <>
                    <span style={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                        {materialAssetType && <>
                            <em style={{marginRight: 8}}>[{materialAssetType}]</em>
                        </>}

                        {materialDescription}
                    </span>

                    <span style={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginLeft: '10px', color: '#666', fontSize: '0.9em'}}>
                        <strong>{t('EXTERNAL.CLIENT.SHIPMENT.TRACKING_ID')} :</strong> <span style={{fontWeight: 'normal'}}>{shipTrackingId || "-"}</span>
                    </span>
                </>}

                {type === 'multiple' && <>
                    <span style={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                        <strong>{t('EXTERNAL.CLIENT.SHIPMENT.TRACKING_ID')} :</strong> <span style={{fontWeight: 'normal'}}>{shipTrackingId || "-"}</span>
                    </span>
                </>}
            </div>

            <div style={{marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '16px'}}>
                {/* Carrier Information Section */}
                <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                    <>
                        {!!shipCarrierName && (
                            <Tooltip title={t('INTERNAL.CLIENT.COMMON.CARRIER')}>
                                <div style={{display: 'flex', alignItems: 'center', gap: '5px'}}>
                                    <Truck style={{fontSize: '16px', color: '#1890ff'}} />
                                    <span style={{maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                                        {shipCarrierName}
                                    </span>
                                </div>
                            </Tooltip>
                        )}

                        {/* Carrier Link */}
                        {!!carrierLink && (
                            <Button
                                href={carrierLink}
                                icon={<ArrowTopRightThinCircleOutline style={{color: '#1890ff'}}/>}
                                type="link"
                                size="small"
                                target={"_blank"}
                                rel={"noreferrer"}
                                style={{padding: '0 5px'}}
                            />
                        )}
                    </>
                </div>

                {/* Status Section */}
                <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                    <Tooltip title={<>
                        <div style={{fontWeight: 'bold'}}>{t('INTERNAL.CLIENT.SHIPMENT.CARRIER_TRACKING_STATUS')}</div>
                        <div>{t('INTERNAL.CLIENT.COMMON.STATUS')}: {shipTrackingStatus}</div>
                        <div>{t('INTERNAL.CLIENT.COMMON.LAST_UPDATED_AT')}: <NpiDisplayDate value={shipTrackingLastUpdateAt} format={"L LT"}/></div>
                    </>}>
                        <Tag color={statusColor} style={{padding: '0 10px', height: '24px', lineHeight: '24px', fontWeight: 500}}>{shipTrackingStatus}</Tag>
                    </Tooltip>
                </div>
            </div>
        </div>
    );
};


interface INpiTabShipmentWaveContentProps {
    wave: IWave,
    shipments: IShipment[],
    carriers: IShipmentCarrier[],
    onChange: (waveId: number, shipments: IShipment[]) => void
}

const NpiTabShipmentWaveContent = ({wave, shipments, carriers, onChange, ...props}: INpiTabShipmentWaveContentProps) => {
    const {t} = useTranslation();

    const shipmentsByTrackingId = useMemo(() => {

        const getLatestDate = (shipment: IShipment) => {
            return new Date(shipment.created_at).getTime();
            // const createdAt = new Date(shipment.created_at).getTime();
            // const updatedAt = shipment.updated_at ? new Date(shipment.updated_at).getTime() : 0;
            // return Math.max(createdAt, updatedAt);
        }

        const sortedShipments = [...shipments].sort((a, b) => (getLatestDate(b) - getLatestDate(a)));

        return _.groupBy(sortedShipments, (shipment: IShipment) => [shipment.carrier, shipment.tracking_id]);

    }, [shipments]);

    // -- When user select choose quick action
    const onSelectQuickAction = (tracking_id: string, action: ShipmentPackageQuickActionsEnum): void => {
        const updatedShipments = _.cloneDeep(shipments);
        updatedShipments.filter(x => x.tracking_id === tracking_id).map((shipment: IShipment) => {

            //@ts-ignore
            switch (ShipmentPackageQuickActionsEnum[action]) {
                case ShipmentPackageQuickActionsEnum.ALL_ASSETS_RECEIVED:
                    shipment.status = ShipmentPosStatusEnum.Received;
                    break;
                case ShipmentPackageQuickActionsEnum.ALL_ASSETS_DAMAGED:
                    shipment.status = ShipmentPosStatusEnum.Damaged;
                    break;
                case ShipmentPackageQuickActionsEnum.ALL_ASSETS_MISSING:
                    shipment.status = ShipmentPosStatusEnum.Missing;
                    break;
                case ShipmentPackageQuickActionsEnum.ALL_ASSETS_INCORRECT:
                    shipment.status = ShipmentPosStatusEnum.Incorrect;
                    break;
                case ShipmentPackageQuickActionsEnum.SHIPMENT_NOT_RECEIVED:
                    shipment.status = ShipmentPosStatusEnum.NotReceived;
                    break;
                default:
                    throw new Error(`Unhandled ${action} action.`);
            }

            return shipment;
        });

        //dispatch changes to parent component
        onChange(wave.id, updatedShipments);
    }


    // -- Change shipment status when changing the package status
    const onPackageStatusChange = (shipment: IShipment, status: ShipmentPosStatusEnum): void => {
        const updatedShipments = _.cloneDeep(shipments);

        const index = updatedShipments.findIndex(s => s.id === shipment.id);

        if (index < 0) {
            return;
        }

        updatedShipments[index].status = status;

        //dispatch changes to parent component
        onChange(wave.id, updatedShipments);
    }

    return <div>
        <Collapse defaultActiveKey={0} accordion>
            {Object.values(shipmentsByTrackingId).map((shipments: IShipment[], index: number) => {
                //find the last updated one
                const shipInfo = _.maxBy(shipments, 'created_at')!;

                const shipTrackingId = shipInfo.tracking_id;
                const shipCarrierName = shipInfo.carrier;
                const carrier = _.find(carriers, {carrier_name: shipCarrierName});
                const carrierLink = !!carrier ? carrier.tracking_url.replace('%code%', shipTrackingId) : '';
                const shipTrackingStatus = shipInfo.receipt_status_converted;
                const shipTrackingLastUpdateAt = shipInfo.created_at;
                const shipMaterialDesc = shipInfo.material_desc;
                const shipAssetType = shipInfo.asset_type;


                let statusColor = 'red';
                switch (shipTrackingStatus) {
                    case 'Awaiting dispatch':
                        statusColor = 'geekblue';
                        break;
                    case 'Delivered':
                        statusColor = 'green';
                        break;
                    case 'Shipped':
                        statusColor = 'orange';
                        break;
                    case 'No Vendor Feedback':
                        statusColor = 'default';
                        break;
                }

                return  <Collapse.Panel
                    key={index}
                    header={
                        <ShipmentHeader
                            count={shipments.length}
                            shipCarrierName={shipCarrierName}
                            shipTrackingId={shipTrackingId}
                            carrierLink={carrierLink}
                            shipTrackingStatus={shipTrackingStatus}
                            statusColor={statusColor}
                            shipTrackingLastUpdateAt={shipTrackingLastUpdateAt}
                            materialDescription={shipMaterialDesc}
                            materialAssetType={shipAssetType}
                        />
                    }
                >

                    {shipments.length > 1 && <div style={{marginBottom: 20, paddingBottom: 20, borderBottom: '0.5px solid lightgray'}}>
                        <strong>{t('EXTERNAL.CLIENT.SHIPMENT.QUICK_PACKAGE_ACTION_PLACEHOLDER')} :</strong>
                        <NpiInputShipmentPackageQuickActionSelect shipments={shipments} onChange={(action: ShipmentPackageQuickActionsEnum) => onSelectQuickAction(shipTrackingId, action)}/>
                    </div>}

                    {shipments.map((shipment: IShipment) =>
                        <div key={shipment.id} style={{marginBottom: 20}}>

                            <div style={{marginBottom: 5}}>

                                {shipment.asset_type && <em style={{marginRight: 8}}>[{shipment.asset_type}]</em>}

                                <span style={{marginRight: 10}}>{shipment.material_desc}</span>

                                <Tooltip title={<ul style={{padding: '0 0 0 20px', margin: 0}}>
                                    <li><strong>{t('INTERNAL.CLIENT.SHIPMENT.MATERIAL_NUMBER')} :</strong> <em>{shipment.material_number}</em></li>
                                    {(shipment.material_quantity && parseInt(shipment.material_quantity) > 0)
                                        ? <li><strong>{t('INTERNAL.CLIENT.SHIPMENT.MATERIAL_QUANTITY')} :</strong> {shipment.material_quantity}</li> : ''}
                                </ul>}>
                                    <InfoCircleOutlined />
                                </Tooltip>
                            </div>

                            {/* Build status from ShipmentPosStatusEnum enum */}
                            <Radio.Group value={shipment.status} onChange={(e: RadioChangeEvent) => onPackageStatusChange(shipment, e.target.value as ShipmentPosStatusEnum)}>
                                {(Object.keys(ShipmentPosStatusEnum) as Array<keyof typeof ShipmentPosStatusEnum>).map((key) => {

                                    let color: string = 'orange';
                                    let icon: React.ReactElement = <MinusOutlined/>;
                                    if( ShipmentPosStatusEnum[key] === ShipmentPosStatusEnum.Received ) {
                                        color = 'green';
                                        icon = <CheckOutlined/>;
                                    } else if( ShipmentPosStatusEnum[key] === ShipmentPosStatusEnum.Damaged ) {
                                        color = 'red';
                                        icon = <CloseOutlined/>;
                                    }

                                    return <Radio.Button key={key} style={{color}} value={ShipmentPosStatusEnum[key]}>
                                        {icon} {t(`EXTERNAL.CLIENT.SHIPMENT.STATUS.${ShipmentPosStatusEnum[key].toUpperCase()}`)}
                                    </Radio.Button>
                                })}
                            </Radio.Group>
                        </div>
                    )}
                </Collapse.Panel>
            })}
        </Collapse>
    </div>
}

export default NpiTabShipmentWaveContent;